import React, { FunctionComponent, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ContentSection,
  Fieldset,
  Heading,
  Hr,
  Layout,
  Notification,
  Paragraph,
  PriceBox,
} from '@vwfs-bronson/bronson-react';
import { FormikHelpers, useFormikContext } from 'formik';
import { CheckboxFormField } from '../../../components/FormFields/CheckboxFormField';
import { NumericFormField } from '../../../components/FormFields/NumericFormField';
import { SelectFormField } from '../../../components/FormFields/SelectFormField';
import { TextFormField } from '../../../components/FormFields/TextFormField';
import { CustomerType, InfoToRenderType, QuoteFlow } from '../../../resources/base/models/enums';
import {
  FinancialProductOption,
  getSortedSupportedFinancialProductOptionsFromOffers,
  isCreditBailProduct,
  isCreditBailProductForOldCar,
  isLCDProduct,
  isLouProduct,
  isLvdlProduct,
  isProductAllowingDamagesWaiver,
  isProductAllowingOverruledRV,
  isProductForOldCar,
  isPushVoProduct,
} from '../../../resources/base/models/financialProduct';
import { FuelType } from '../../../resources/base/models/vehicleTypes';
import { Analytics } from '../../../services/analytics';
import { productConfigurationPageEvent } from '../../../services/analytics/analyticsEvents';
import { getQuoteQuotationTemplates } from '../../../services/api/miles/miles-services';
import {
  appendMissingDecimalPart,
  formatStringNumber,
  roundNumber,
  stringNumberToNumber,
} from '../../../services/common/formatNew';
import { assertDefined } from '../../../services/common/miscUtils';
import { RootState } from '../../../services/redux';
import { setSelectedFinancialProductData } from '../../../services/redux/features/custom.slice';
import { customerSelectors } from '../../../services/redux/features/customer.slice';
import { loadingActions } from '../../../services/redux/features/loading.slice';
import {
  quoteSelectors,
  setIsPushVoWarningShown,
  setProductTypeRestore,
} from '../../../services/redux/features/quote.slice';
import { useNavigate } from '../../../services/routing/navigate';
import { QuotationFormData } from '../../../types';
import { benefitPackageComponentType } from '../../../types/quotationData';
import { handleCopyQuote } from '../../utils';
import {
  calculateAnnualMileage,
  calculateContractMileage,
  calculateDownPaymentAmount,
  calculateDownPaymentAmountPercentage,
  calculateFinancedAmount,
  calculateFirstInstallmentAmount,
  calculateFirstInstallmentAmountPercentage,
  calculateMaxEligibility,
} from './calculations';
import { CopyQuoteModal } from './CopyQuoteModal';
import { CustomModal } from './CustomModal';
import { DocumentsPrintAndEmailSendoutModal } from './DocumentsPrintAndEmailSendoutModal';
import {
  buildModalConfirmText,
  buildModalTitle,
  DamagesWaiver,
  DistributionModel,
  Duration,
  getModalCenterContent,
  MilesPackage,
  ModalOpenButton,
  SaveAndQuoteDocumentsButtons,
  Services,
  TotalAmountInsAndServ,
  WithdrawalDate,
} from './fieldset';
import { PricingStrategyCombobox } from './PricingStrategyCombobox';
import {
  adjustMileageToAllowedRange,
  generateEligibilityDropdownOptions,
  getAllowedValuesByRestriction,
  getCalculateRequest,
  getPermittedAdditionalProductsFromQuote,
  InsuranceCustomerQualifierId,
  isAudiPromoEligibilityEditable,
  maxDownPaymentAmountPercentage,
  prefillQuotationForm,
  setAdditionalProductsInsuranceCustomerQualifiers,
  setAdditionalProductsTireServicesQualifiers,
  setInsuranceCustomerQualifierFromResponse,
  stripPromotionsFromValues,
  useQuoteCalculation,
} from './utils';
import { VehicleDetails } from './VehicleDetails';

type CalculateQuoteOnDiscountChange = (discount: {
  totalDiscountAmount: number;
  totalDiscountPercentage: number;
  downPaymentAmount?: number;
}) => Promise<QuoteSchema.Schemas.QuoteResponse | undefined>;

type CalculateQuoteOnContractRVChange = (contractRV: {
  contractRVAmount: number;
  contractRVPercentage: number;
}) => Promise<QuoteSchema.Schemas.QuoteResponse | undefined>;

type CalculateQuoteOnOverruledRVChange = (
  overruledRVGrosAmount: number
) => Promise<QuoteSchema.Schemas.QuoteResponse | undefined>;

interface Props {
  quoteId: string;
}

const createFormInputs = ((setFieldValue: FormikHelpers<QuotationFormData>['setFieldValue']) => {
  return {
    contractMileage: (isDisabled: boolean, mileageAllowedValues: number[]) => (
      <NumericFormField
        fieldName="contractMileage"
        labelTranslationKey="step3:contractMileage:label"
        type="km"
        maximumFractionDigits={0}
        rounding={5000}
        onValueChange={(contractMileage: number, values: QuotationFormData) => {
          const newContractMileage: number = adjustMileageToAllowedRange(contractMileage, mileageAllowedValues);
          setFieldValue('contractMileage', newContractMileage);
          setFieldValue('annualMileage', calculateAnnualMileage(newContractMileage, values.duration));
        }}
        isDisabled={isDisabled}
      />
    ),
    annualMileage: (isDisabled: boolean, mileageAllowedValues: number[]) => (
      <NumericFormField
        fieldName="annualMileage"
        labelTranslationKey="step3:annualMileage:label"
        type="km"
        maximumFractionDigits={0}
        isDisabled={isDisabled}
        onValueChange={(annualMileage: number, values: QuotationFormData) => {
          const newContractMileage: number = adjustMileageToAllowedRange(
            calculateContractMileage(annualMileage, values.duration),
            mileageAllowedValues
          );
          setFieldValue('contractMileage', newContractMileage);
          // Recalculate annualMileage after contractMileage has been calculated and rounded
          setFieldValue('annualMileage', calculateAnnualMileage(newContractMileage, values.duration));
        }}
      />
    ),
    financedAmount: (
      isDisabled: boolean,
      isCarUsed: boolean,
      calculateOnDiscountChange: CalculateQuoteOnDiscountChange
    ) => (
      <>
        <NumericFormField
          fieldName="financedAmount"
          labelTranslationKey="step3:financedAmount:label"
          type="money"
          maximumFractionDigits={2}
          isReadonly={isCarUsed && !isDisabled}
          isDisabled={isDisabled}
          onValueChange={async (newFinancedAmount, values) => {
            const quote = await calculateOnDiscountChange({ totalDiscountPercentage: 0, totalDiscountAmount: 0 });
            if (!quote) return;

            const newDiscountAmount = roundNumber(Math.max(0, values.vehicleTotalCost - newFinancedAmount));
            calculateOnDiscountChange({ totalDiscountPercentage: 0, totalDiscountAmount: newDiscountAmount });
          }}
        />
      </>
    ),
    recommendedRV: () => (
      <NumericFormField
        fieldName="recommendedRV"
        labelTranslationKey="step3:recommendedRV:label"
        type="money"
        maximumFractionDigits={2}
        isDisabled
        optional
      />
    ),
    overruledRV: (
      isDisabled: boolean,
      isLCDProduct: boolean,
      calculateOnOverruledRVChange?: CalculateQuoteOnOverruledRVChange
    ) => (
      <NumericFormField
        fieldName="overruledRV"
        labelTranslationKey={isLCDProduct ? 'step3:overruledRVGross:label' : 'step3:overruledRV:label'}
        type="money"
        maximumFractionDigits={2}
        isDisabled={isDisabled}
        onValueChange={async (overruledRV) => {
          if (isLCDProduct && calculateOnOverruledRVChange) {
            calculateOnOverruledRVChange(overruledRV);
          }
        }}
      />
    ),
    overruledRVNet: () => (
      <NumericFormField
        fieldName="overruledRVNetAmount"
        labelTranslationKey="step3:overruledRVNet:label"
        type="money"
        maximumFractionDigits={2}
        isDisabled
      />
    ),
    overruledRVPercentage: () => (
      <NumericFormField
        fieldName="overruledRVPercentage"
        labelTranslationKey="step3:overruledRVPercentage:label"
        type="percentage"
        maximumFractionDigits={2}
        isDisabled
      />
    ),
    vehicleTotalCost: () => (
      <NumericFormField
        fieldName="vehicleTotalCost"
        labelTranslationKey="step3:vehicleTotalCost:label"
        type="money"
        maximumFractionDigits={2}
        isDisabled
      />
    ),
    excessMileagePriceAmount: (isDisabled: boolean) => (
      <NumericFormField
        fieldName="excessMileagePriceAmount"
        labelTranslationKey="step3:excessMileagePriceAmount:label"
        type="money"
        maximumFractionDigits={2}
        isDisabled={isDisabled}
      />
    ),
    totalDiscountAmount: (
      minValue: number,
      calculateOnDiscountChange: CalculateQuoteOnDiscountChange,
      isDisabled: boolean
    ) => (
      <NumericFormField
        fieldName="totalDiscountAmount"
        labelTranslationKey="step3:totalDiscountAmount:label"
        type="money"
        maximumFractionDigits={2}
        isDisabled={isDisabled}
        optional
        minValue={minValue}
        onValueChange={async (totalDiscountAmount: number, values: QuotationFormData) => {
          const newCalculatedDownPaymentAmount = roundNumber((values.vehicleTotalCost - totalDiscountAmount) * 0.01);
          setFieldValue('totalDiscountPercentage', 0);
          calculateOnDiscountChange({
            totalDiscountPercentage: 0,
            totalDiscountAmount,
            downPaymentAmount: newCalculatedDownPaymentAmount,
          });
        }}
      />
    ),
    totalDiscountPercentage: (
      minValue: number,
      calculateOnDiscountChange: CalculateQuoteOnDiscountChange,
      isDisabled: boolean
    ) => (
      <NumericFormField
        isDisabled={isDisabled}
        fieldName="totalDiscountPercentage"
        labelTranslationKey="step3:totalDiscountPercentage:label"
        type="percentage"
        maximumFractionDigits={2}
        minValue={minValue}
        onValueChange={async (totalDiscountPercentage) => {
          setFieldValue('totalDiscountAmount', 0);
          calculateOnDiscountChange({ totalDiscountAmount: 0, totalDiscountPercentage });
        }}
      />
    ),
    firstInstallmentAmount: (isDisabled: boolean, maxValue: number) => (
      <NumericFormField
        fieldName="firstInstallmentAmount"
        labelTranslationKey="step3:firstInstallmentAmount:label"
        type="money"
        maximumFractionDigits={2}
        isDisabled={isDisabled}
        optional
        maxValue={maxValue}
        onValueChange={(firstInstallmentAmount: number, values: QuotationFormData) => {
          setFieldValue(
            'firstInstallmentAmountPercentage',
            calculateFirstInstallmentAmountPercentage(firstInstallmentAmount, values.financedAmount)
          );
        }}
      />
    ),
    firstInstallmentAmountPercentage: (isDisabled: boolean) => (
      <NumericFormField
        fieldName="firstInstallmentAmountPercentage"
        type="percentage"
        // Whitespace character as label to have inputfield aligned with others
        labelTranslationKey="&nbsp;"
        maximumFractionDigits={2}
        maxValue={100}
        isDisabled={isDisabled}
        optional
        onValueChange={(firstInstallmentAmountPercentage: number, values: QuotationFormData) => {
          setFieldValue(
            'firstInstallmentAmount',
            calculateFirstInstallmentAmount(firstInstallmentAmountPercentage, values.financedAmount)
          );
        }}
      />
    ),
    totalServicesCost: () => (
      <NumericFormField
        fieldName="totalServicesCost"
        labelTranslationKey="step3:totalServicesCost:label"
        type="money"
        maximumFractionDigits={2}
        isDisabled
        optional
      />
    ),
    subsequentInstallment: () => (
      <NumericFormField
        fieldName="subsequentInstallment"
        labelTranslationKey="step3:subsequentInstallment:label"
        type="money"
        maximumFractionDigits={2}
        isDisabled
        optional
      />
    ),
    firstInstallmentWithDownPaymentAmount: () => (
      <NumericFormField
        fieldName="firstInstallmentWithDownPaymentAmount"
        labelTranslationKey="step3:firstInstallmentWithDownPaymentAmount:label"
        type="money"
        maximumFractionDigits={2}
        isDisabled
        optional
      />
    ),
    firstInstallmentWithDownPaymentPercentage: () => (
      <NumericFormField
        fieldName="firstInstallmentWithDownPaymentPercentage"
        // Whitespace character as label to have inputfield aligned with others
        labelTranslationKey="&nbsp;"
        type="percentage"
        maximumFractionDigits={2}
        isDisabled
        optional
      />
    ),
    downPaymentAmount: (isDisabled: boolean, maxValue: number) => (
      <NumericFormField
        fieldName="downPaymentAmount"
        labelTranslationKey="step3:downPaymentAmount:label"
        type="money"
        maximumFractionDigits={2}
        isDisabled={isDisabled}
        maxValue={maxValue}
        optional
        onValueChange={(downPaymentAmount: number, values: QuotationFormData) => {
          setFieldValue(
            'downPaymentAmountPercentage',
            calculateDownPaymentAmountPercentage(downPaymentAmount, values.financedAmount)
          );
        }}
      />
    ),
    downPaymentAmountPercentage: (isDisabled: boolean, maxValue: number) => (
      <NumericFormField
        fieldName="downPaymentAmountPercentage"
        // Whitespace character as label to have inputfield aligned with others
        labelTranslationKey="&nbsp;"
        type="percentage"
        maximumFractionDigits={2}
        isDisabled={isDisabled}
        maxValue={maxValue}
        optional
        onValueChange={(downPaymentAmountPercentage: number, values: QuotationFormData) => {
          setFieldValue(
            'downPaymentAmount',
            calculateDownPaymentAmount(downPaymentAmountPercentage, values.financedAmount)
          );
        }}
      />
    ),
    milesBonusEcologic: (isDisabled: boolean) => (
      <NumericFormField
        fieldName="milesBonusEcologic"
        labelTranslationKey="step3:milesBonusEcologic:label"
        type="money"
        maximumFractionDigits={2}
        isDisabled={isDisabled}
        optional
      />
    ),
    milesPrimeCee: (isDisabled: boolean) => (
      <NumericFormField
        fieldName="milesPrimeCee"
        labelTranslationKey="step3:primeCee:label"
        type="money"
        maximumFractionDigits={2}
        isDisabled={isDisabled}
        optional
        maxValue={10000}
      />
    ),
    serviceBonus: () => (
      <NumericFormField
        fieldName="serviceBonus"
        labelTranslationKey="step3:serviceBonus:label"
        type="percentage"
        maximumFractionDigits={2}
        isDisabled
        optional
      />
    ),
    eligibility: (
      maxValue: number,
      isEditable: boolean,
      dropdownOptions: { value: string; label: string }[],
      onEligibilityChange: (newEligibility: number) => void
    ) =>
      isEditable ? (
        <SelectFormField
          fieldName="eligibility"
          labelTranslationKey="step3:eligibility:label"
          dropDownOptionsList={dropdownOptions}
          optional
          onChange={(event) => onEligibilityChange(Number(event.target.value))}
        />
      ) : (
        <NumericFormField
          fieldName="eligibility"
          labelTranslationKey="step3:eligibility:label"
          type="percentage"
          maximumFractionDigits={2}
          isDisabled
          maxValue={maxValue}
          optional
        />
      ),
    dealerEffort: (isDisabled: boolean, maxValue: number) => (
      <NumericFormField
        fieldName="dealerEffort"
        labelTranslationKey="step3:dealerEffort:label"
        type="percentage"
        maximumFractionDigits={2}
        isDisabled={isDisabled}
        maxValue={maxValue}
        optional
      />
    ),
    dealerEffortMax: () => (
      <NumericFormField
        fieldName="dealerEffortMax"
        labelTranslationKey="step3:dealerEffortMax:label"
        type="percentage"
        maximumFractionDigits={2}
        isDisabled
        optional
      />
    ),
    firstInstallmentIsIncreased: (isDisabled: boolean) => (
      <CheckboxFormField
        fieldName="firstInstallmentIsIncreased"
        labelTranslationKey="step3:firstInstallmentIsIncreased:label"
        isDisabled={isDisabled}
      />
    ),
    pricingId: () => (
      <TextFormField isDisabled fieldName="pricingId" optional labelTranslationKey="step3:pricing:pricingId:label" />
    ),
    numberOfMonthlyInstallments: () => (
      <NumericFormField
        fieldName="numberOfMonthlyInstallments"
        labelTranslationKey="step3:numberOfMonthlyInstallments:label"
        type="numeric"
        maximumFractionDigits={0}
        isDisabled
        optional
      />
    ),
    fuelCardChosen: (isDisabled: boolean) => (
      <CheckboxFormField
        isDisabled={isDisabled}
        fieldName="fuelCardChosen"
        labelTranslationKey="step3:fuelCardChosen:label"
      />
    ),
    contractRVAmount: (isDisabled: boolean, calculateOnContractRVChange: CalculateQuoteOnContractRVChange) => (
      <NumericFormField
        fieldName="contractRVAmount"
        labelTranslationKey="step3:contractRVAmount:label"
        type="money"
        maximumFractionDigits={2}
        isDisabled={isDisabled}
        onValueChange={async (contractRVAmount) => {
          setFieldValue('contractRVPercentage', 0);
          calculateOnContractRVChange({ contractRVAmount, contractRVPercentage: 0 });
        }}
      />
    ),
    contractRVPercentage: (isDisabled: boolean, calculateOnContractRVChange: CalculateQuoteOnContractRVChange) => (
      <NumericFormField
        fieldName="contractRVPercentage"
        labelTranslationKey="step3:contractRVPercentage:label"
        type="percentage"
        maximumFractionDigits={2}
        isDisabled={isDisabled}
        onValueChange={async (contractRVPercentage) => {
          setFieldValue('contractRVAmount', 0);
          calculateOnContractRVChange({ contractRVAmount: 0, contractRVPercentage });
        }}
      />
    ),
    financialProduct: (
      isDisabled: boolean,
      financialProductsOptions: FinancialProductOption[],
      handleProductChange: React.ChangeEventHandler<HTMLSelectElement>
    ) => (
      <SelectFormField
        fieldName="financialProduct"
        labelTranslationKey="step3:financialProduct:label"
        dropDownOptionsList={financialProductsOptions}
        isDisabled={isDisabled}
        onChange={handleProductChange}
      />
    ),
    annualDepreciationNonDeductible: (isDisabled = true) => (
      <NumericFormField
        fieldName="annualDepreciationNonDeductible"
        labelTranslationKey="step3:annualDepreciationNonDeductible:label"
        type="money"
        isDisabled={isDisabled}
        maximumFractionDigits={2}
        optional
      />
    ),
    fringeBenefitFuelIncluded: (isDisabled = true) => (
      <NumericFormField
        fieldName="fringeBenefitFuelIncluded"
        labelTranslationKey="step3:fringeBenefitFuelIncluded:label"
        isDisabled={isDisabled}
        type="money"
        maximumFractionDigits={2}
        optional
      />
    ),
    fringeBenefitFuelExcluded: (isDisabled = true) => (
      <NumericFormField
        fieldName="fringeBenefitFuelExcluded"
        labelTranslationKey="step3:fringeBenefitFuelExcluded:label"
        isDisabled={isDisabled}
        type="money"
        maximumFractionDigits={2}
        optional
      />
    ),
    fringeBenefitElectricCars: (isDisabled = true) => (
      <NumericFormField
        fieldName="fringeBenefitElectricCars"
        labelTranslationKey="step3:fringeBenefitElectricCars:label"
        isDisabled={isDisabled}
        type="money"
        maximumFractionDigits={2}
        optional
      />
    ),
    annualCo2Tax: (isDisabled = true) => (
      <NumericFormField
        fieldName="annualCo2Tax"
        labelTranslationKey="step3:annualCo2Tax:label"
        isDisabled={isDisabled}
        type="money"
        maximumFractionDigits={2}
        optional
      />
    ),
    annualTaxOnAirPollutants: (isDisabled = true) => (
      <NumericFormField
        fieldName="annualTaxOnAirPollutants"
        labelTranslationKey="step3:annualTaxOnAirPollutants:label"
        isDisabled={isDisabled}
        type="money"
        maximumFractionDigits={2}
        optional
      />
    ),
  };
}) satisfies (...args: any[]) => Partial<Record<keyof QuotationFormData, (...args: any[]) => ReactElement>>;

export const QuotationFinancialProductForm: FunctionComponent<Props> = (props) => {
  const { quoteId } = props;
  const navigate = useNavigate();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { values, setFieldValue, resetForm } = useFormikContext<QuotationFormData>();
  const customerType = assertDefined(useSelector(customerSelectors.selectCustomerType));
  const hasCoCustomer = useSelector(customerSelectors.selectHasCoCustomer);
  const legalEntity = useSelector(customerSelectors.selectLegalEntity);

  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [openPrintAndSaveModal, setOpenPrintAndSaveModal] = useState(false);
  const [openCopyQuoteModal, setOpenCopyQuoteModal] = useState(false);
  const [financialProductsOptions, setFinancialProductsOptions] = useState<FinancialProductOption[]>([]);
  const quote = useSelector(quoteSelectors.selectQuote);
  const isElectricEngine = quote?.vehicleData?.model?.engine?.fuelTypeLabel === FuelType.Electric;

  const quoteFlow = useSelector(quoteSelectors.selectQuoteFlow);
  const isFormDisabled = !useSelector(quoteSelectors.selectIsQuoteEditable);
  const [openModal, setOpenModal] = useState(false);
  const [infoToRender, setInfoToRender] = useState<InfoToRenderType>();
  const [additionalProducts, setAdditionalProducts] = useState<QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[]>(
    []
  );
  const [selectedAllAdditionalProducts, setSelectedAllAdditionalProducts] = useState<
    QuoteSchema.Schemas.AdditionalProductsGetQuoteItem[]
  >([]);
  const [inputs] = useState<ReturnType<typeof createFormInputs>>(() => createFormInputs(setFieldValue));
  const [durationAllowedValues, setDurationAllowedValues] = useState<number[]>([]);
  const [mileageAllowedValues, setMileageAllowedValues] = useState<number[]>([]);
  const [fuelDepositAmount, setFuelDepositAmount] = useState<number>(0);
  const [managmentFees, setManagementFees] = useState<number>(0);
  const [isInitialPrefilDone, setIsInitialPrefilDone] = useState(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const productTypeRestore = useSelector((state: RootState) => state.quote.productTypeRestore);
  const [showPrimeCee, setShowPrimeCee] = useState(false);
  const [selectedInsuranceApplicantOption, setSelectedInsuranceApplicantOption] = useState(
    InsuranceCustomerQualifierId.APPLICANT
  );

  const promotions = useMemo(
    () => quote?.financialProduct?.offers?.[0]?.promotions ?? [],
    [quote?.financialProduct?.offers]
  );

  const baremeStartsWithAsl =
    promotions
      .find((promotion) => promotion.milesPromotionId === values.selectedPromotions?.[0])
      ?.milesExternalReference?.startsWith('ASL') || false;
  // PC-3510: we should select only the first benefit package, as per ticket
  const benefitPackage = quote?.financialProduct?.offers?.[0]?.benefitPackages?.[0];

  const {
    calculateQuoteAuto,
    calculateQuoteManualFinal,
    isFormSyncedWithApi,
    isCalculationArbitrarilyRequired,
    milesWarnings,
    resetMilesWarnings,
  } = useQuoteCalculation(isInitialPrefilDone, selectedAllAdditionalProducts);

  const isCarUsed = useSelector((state: RootState) => state.custom.isCarUsed);
  const newCopiedQuoteSubmitted = useSelector(quoteSelectors.selectIsNewCopiedQuoteSubmitted);
  const pushVoWarningShown = useSelector(quoteSelectors.selectIsPushVoWarningShown);

  useEffect(() => {
    if (!isInitialPrefilDone) return;
    setFieldValue('financedAmount', calculateFinancedAmount(values.vehicleTotalCost, values.totalDiscountAmount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.totalDiscountAmount, values.vehicleTotalCost]);

  useEffect(() => {
    if (Number(values.milesPrimeCee) > values.firstInstallmentAmount) {
      setFieldValue('milesPrimeCee', values.firstInstallmentAmount);
    }
  }, [values.milesPrimeCee, values.firstInstallmentAmount, setFieldValue]);

  useEffect(() => {
    const durationOver24 = (values.duration || 0) > 24;
    const isStockOrBto = !isCarUsed;
    setShowPrimeCee(
      durationOver24 &&
        isElectricEngine &&
        isStockOrBto &&
        !isLCDProduct(values.financialProduct) &&
        !baremeStartsWithAsl
    );
  }, [values.duration, isElectricEngine, isCarUsed, values.financialProduct, baremeStartsWithAsl]);

  useEffect(() => {
    if (!isInitialPrefilDone || !benefitPackage) return;
    const { components } = benefitPackage;

    components?.forEach((component) => {
      // provided values are monthly, we want to display annually
      const componentValue = ((component.mainPeriodAmount || component.alternativePeriodAmount) ?? 0) * 12;

      switch (component.milesComponentTypeId) {
        case benefitPackageComponentType.ANNUAL_DEPRECIATION_NON_DEDUCTIBLE:
          setFieldValue('annualDepreciationNonDeductible', componentValue);
          break;
        case benefitPackageComponentType.FRINGE_BENEFIT_FUEL_INCLUDED:
          setFieldValue('fringeBenefitFuelIncluded', componentValue);
          break;
        case benefitPackageComponentType.FRINGE_BENEFIT_FUEL_EXCLUDED:
          setFieldValue('fringeBenefitFuelExcluded', componentValue);
          break;
        case benefitPackageComponentType.FRINGE_BENEFIT_ELECTRIC_CARS:
          setFieldValue('fringeBenefitElectricCars', componentValue);
          break;
        case benefitPackageComponentType.ANNUAL_CO2_TAX:
          setFieldValue('annualCo2Tax', componentValue);
          break;
        case benefitPackageComponentType.ANNUAL_TAX_ON_AIR_POLLUTANTS:
          setFieldValue('annualTaxOnAirPollutants', componentValue);
          break;
        default:
          break;
      }
    });
  }, [benefitPackage, isInitialPrefilDone, setFieldValue]);

  useEffect(() => {
    setShowNotification(!!milesWarnings?.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milesWarnings]);

  useEffect(() => {
    if (!isInitialPrefilDone) return;
    const firstInstallmentWithDownPaymentAmount = values.downPaymentAmount + values.firstInstallmentAmount;
    const firstInstallmentWithDownPaymentPercentage =
      (100 * firstInstallmentWithDownPaymentAmount) / values.financedAmount;

    setFieldValue('firstInstallmentWithDownPaymentAmount', firstInstallmentWithDownPaymentAmount);
    setFieldValue('firstInstallmentWithDownPaymentPercentage', firstInstallmentWithDownPaymentPercentage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.downPaymentAmount, values.firstInstallmentAmount]);

  useEffect(() => {
    if (!isInitialPrefilDone) return;
    setFieldValue(
      'totalAmountInsAndServ',
      roundNumber(values.downPaymentAmount + values.firstInstallmentAmount + values.totalServicesCost)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.downPaymentAmount, values.firstInstallmentAmount, values.totalServicesCost]);

  useEffect(() => {
    if (!isInitialPrefilDone) return;
    const isWarrantyOrIncreasedRentProvided = values.downPaymentAmount > 0 || values.firstInstallmentAmount > 0;

    if (isWarrantyOrIncreasedRentProvided) {
      setFieldValue('numberOfMonthlyInstallments', stringNumberToNumber(values.duration) - 1);
    } else {
      setFieldValue('numberOfMonthlyInstallments', stringNumberToNumber(values.duration));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.downPaymentAmount, values.firstInstallmentAmount, values.duration]);

  useEffect(() => {
    if (!isInitialPrefilDone) return;
    const newEligiblity = roundNumber((values.serviceBonus * values.dealerEffort) / 100);

    if (values.eligibility !== newEligiblity) {
      setFieldValue('eligibility', newEligiblity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.serviceBonus, values.dealerEffort]);

  useEffect(() => {
    setAdditionalProducts(getPermittedAdditionalProductsFromQuote(quote));

    const { milesFuelCardAmount } = quote?.financialProduct?.offers?.[0].calculation || {};
    if (milesFuelCardAmount) {
      setFuelDepositAmount((milesFuelCardAmount.netAmount || 0) + (milesFuelCardAmount.vatAmount || 0));
    }

    setManagementFees(quote?.financialProduct?.offers?.[0].calculation?.milesFuelDepositAmount?.netAmount || 0);
  }, [quote]);

  useEffect(() => {
    if (!isInitialPrefilDone) return;
    setSelectedAllAdditionalProducts([...additionalProducts]);
  }, [additionalProducts, isInitialPrefilDone]);

  useEffect(() => {
    setInsuranceCustomerQualifierFromResponse(additionalProducts, setSelectedInsuranceApplicantOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalProducts, setSelectedAllAdditionalProducts]);

  useEffect(() => {
    if (!isInitialPrefilDone) return;

    setSelectedAllAdditionalProducts((prevAdditionalProducts) => {
      return setAdditionalProductsTireServicesQualifiers(prevAdditionalProducts, values, setFieldValue);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.tireCountSummer, values.tireCountWinter, values.tireCountAllSeasons, isInitialPrefilDone, setFieldValue]);

  useEffect(() => {
    if (!isInitialPrefilDone) return;

    setSelectedAllAdditionalProducts((prevAdditionalProducts) => {
      return setAdditionalProductsInsuranceCustomerQualifiers(prevAdditionalProducts, selectedInsuranceApplicantOption);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInsuranceApplicantOption]);

  /**
   * - Setup duration and mileage values based on quotation template restrictions.
   */
  const setDurationAndMileageAllowedAndInitialValues = async (_selectedProduct: FinancialProductOption | undefined) => {
    const restrictions: Partial<QuoteSchema.Schemas.OfferRestriction>[] | undefined = _selectedProduct?.restrictions;

    const durationAllowedValues = getAllowedValuesByRestriction(restrictions?.[0]);
    const mileageAllowedValues = getAllowedValuesByRestriction(restrictions?.[1]);

    const duration = _selectedProduct?.duration ?? Number(restrictions?.[0]?.defaultValue || 0);
    const mileage = _selectedProduct?.contractMileage ?? Number(restrictions?.[1]?.defaultValue || 0);

    // Include the duration from Miles in the allowed values
    if (!durationAllowedValues.includes(duration)) {
      durationAllowedValues.push(duration);
      durationAllowedValues.sort((durationA, durationB) => durationA - durationB);
    }

    setDurationAllowedValues(durationAllowedValues);
    setMileageAllowedValues(mileageAllowedValues);
    setFieldValue('duration', duration);
    setFieldValue('contractMileage', mileage);
    setFieldValue('annualMileage', calculateAnnualMileage(mileage, duration));
  };

  useEffect(() => {
    const initializeForm = async () => {
      const isCopyOrModifyFlow = quoteFlow !== QuoteFlow.NEW;

      if (!quoteId) return;
      dispatch(loadingActions.setLoading(true));

      const offers = (await getQuoteQuotationTemplates(quoteId)).financialProduct.offers ?? [];

      dispatch(loadingActions.setLoading(false));

      const sortedSupportedFinancialProductOptions = getSortedSupportedFinancialProductOptionsFromOffers(
        offers,
        customerType,
        isCarUsed,
        legalEntity
      );

      let _selectedProduct = {} as FinancialProductOption | undefined;

      const existingOffer = quote.financialProduct?.offers?.[0];

      if (existingOffer) {
        _selectedProduct = sortedSupportedFinancialProductOptions.find(
          ({ milesTemplateId }) => milesTemplateId === existingOffer.milesTemplateId
        );
      }

      if (isCopyOrModifyFlow && !newCopiedQuoteSubmitted) {
        if (!existingOffer) throw new Error('Copied or modified quote is missing offer.');

        if (!_selectedProduct) throw new Error('Copied or modified quote relates to inexistent template.');
      }

      if (_selectedProduct && existingOffer) {
        _selectedProduct.duration = existingOffer.calculation?.duration;
        _selectedProduct.contractMileage = existingOffer.calculation?.contractMileage;
      } else {
        // eslint-disable-next-line prefer-destructuring
        _selectedProduct = sortedSupportedFinancialProductOptions[0];
      }

      setFinancialProductsOptions(sortedSupportedFinancialProductOptions);
      setSelectedProduct(_selectedProduct?.value ?? '');
      dispatch(setSelectedFinancialProductData(_selectedProduct));

      setFieldValue('financialProduct', _selectedProduct?.value);

      prefillQuotationForm(quote, setFieldValue);

      setFieldValue('milesTemplateId', _selectedProduct?.milesTemplateId);

      setDurationAndMileageAllowedAndInitialValues(_selectedProduct);

      const _additionalProducts = getPermittedAdditionalProductsFromQuote(quote);
      setAdditionalProducts(_additionalProducts);

      const _selectedAllAdditionalProducts = setAdditionalProductsTireServicesQualifiers(
        _additionalProducts,
        values,
        setFieldValue
      );
      setSelectedAllAdditionalProducts(_selectedAllAdditionalProducts);

      setIsInitialPrefilDone(true);
    };

    initializeForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  const resetFuelPrices = useCallback(() => {
    setManagementFees(0);
    setFuelDepositAmount(0);
  }, []);

  const showOverruledRVs = isProductAllowingOverruledRV(selectedProduct);

  const handleProductChange: React.ChangeEventHandler<HTMLSelectElement> = async (e) => {
    setSelectedProduct(e.target.value);
    const selectedProduct = financialProductsOptions.find((product) => product.value === e.target.value);

    const { distributionModel } = values;

    await resetForm();

    const financialProduct = selectedProduct?.value;

    if (!financialProduct) throw new Error('Missing required financialProduct data.');

    setFieldValue('financialProduct', financialProduct);
    setFieldValue('distributionModel', distributionModel);

    dispatch(setSelectedFinancialProductData(selectedProduct));

    setFieldValue('milesTemplateId', selectedProduct?.milesTemplateId);

    if (isLCDProduct(financialProduct)) {
      setOpenModal(true);
      setInfoToRender(InfoToRenderType.LCD_WARNING);
    }

    setDurationAndMileageAllowedAndInitialValues(selectedProduct);
  };

  useEffect(() => {
    if (productTypeRestore) {
      setSelectedProduct(productTypeRestore.productTypeValue);
      const selectedProduct = financialProductsOptions.find(
        (product) => product.value === productTypeRestore.productTypeValue
      );
      const financialProduct = selectedProduct?.value;

      if (!financialProduct) throw new Error('Missing required financialProduct data.');

      setFieldValue('financialProduct', financialProduct);

      dispatch(setProductTypeRestore(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTypeRestore]);

  useEffect(() => {
    if (pushVoWarningShown || !isPushVoProduct(selectedProduct)) return;

    setOpenModal(true);
    setInfoToRender(InfoToRenderType.PUSH_VO_WARNING);
    dispatch(setIsPushVoWarningShown(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  const onClickOnCalculateTotal = async () => {
    calculateQuoteManualFinal(
      quoteId,
      getCalculateRequest(
        isFormSyncedWithApi ? values : stripPromotionsFromValues(values),
        selectedAllAdditionalProducts
      )
    );
    Analytics.push(productConfigurationPageEvent);
  };

  const calculateOnDiscountChange: CalculateQuoteOnDiscountChange = (discount) => {
    return calculateQuoteAuto(
      quoteId,
      getCalculateRequest({
        ...stripPromotionsFromValues(values),
        totalDiscountAmount: discount.totalDiscountAmount,
        totalDiscountPercentage: discount.totalDiscountPercentage,
        downPaymentAmount: discount.downPaymentAmount ?? 0,
      })
    );
  };

  const calculateOnContractRVChange: CalculateQuoteOnContractRVChange = (contractRV) => {
    return calculateQuoteAuto(
      quoteId,
      getCalculateRequest({
        ...stripPromotionsFromValues(values),
        contractRVAmount: contractRV.contractRVAmount,
        contractRVPercentage: contractRV.contractRVPercentage,
      })
    );
  };

  const calculateOnOverruledRVChange: CalculateQuoteOnOverruledRVChange = (overruledRVGrossAmount) => {
    return calculateQuoteAuto(
      quoteId,
      getCalculateRequest({
        ...stripPromotionsFromValues(values),
        overruledRV: overruledRVGrossAmount,
      })
    );
  };

  const isLouProductSelected = isLouProduct(selectedProduct);
  const isLvdlProductSelected = isLvdlProduct(selectedProduct);
  const isCreditBailProductSelected = isCreditBailProduct(selectedProduct);
  const isProductForOldCarSelected = isProductForOldCar(selectedProduct);
  const isCreditBailProductForOldCarSelected = isCreditBailProductForOldCar(selectedProduct);
  const isLCDProductSelected = isLCDProduct(selectedProduct);
  // const isLCDProductSelected = true;

  const showDamagesWaiverField = isProductAllowingDamagesWaiver(selectedProduct, customerType);

  const maxDownPaymentAmount = calculateDownPaymentAmount(maxDownPaymentAmountPercentage, values.financedAmount);

  const maxEligiblity = calculateMaxEligibility(values.dealerEffortMax, values.serviceBonus);

  const isAudiPromoEligibilityEdit = isAudiPromoEligibilityEditable(
    quote?.vehicleData?.model?.manufacturer,
    selectedProduct,
    customerType,
    values.milesPricingStrategy,
    values.serviceBonus
  );

  const eligibilityDropdownOptions = isAudiPromoEligibilityEdit
    ? generateEligibilityDropdownOptions(values.serviceBonus)
    : [];

  const handleEligibilityChange = (newEligibility: number) => {
    if (!values.serviceBonus) return;

    const newDealerEffort = roundNumber((newEligibility * 100) / values.serviceBonus);

    setFieldValue('dealerEffort', newDealerEffort);
    calculateQuoteAuto(
      quoteId,
      getCalculateRequest(
        stripPromotionsFromValues({ ...values, dealerEffort: newDealerEffort }),
        selectedAllAdditionalProducts
      )
    );
  };

  const spreadLeasePrice = `${appendMissingDecimalPart(formatStringNumber(values.spreadLeasePrice), 2)} €`;

  const isQuoteEditable = useSelector(quoteSelectors.selectIsQuoteEditable);
  const isQuoteUnlockable = useSelector(quoteSelectors.selectIsQuoteUnlockable);

  const canValidate = isQuoteEditable && isFormSyncedWithApi && !isCalculationArbitrarilyRequired;
  const shouldCalculate = !canValidate && !isQuoteUnlockable;

  const renderEligibilitySection = (
    <Layout.Item default="1/4" s="1/1">
      <Layout>
        <Layout.Item default="1/1" s="1/1">
          {inputs.eligibility(
            maxEligiblity,
            !isFormDisabled && isAudiPromoEligibilityEdit,
            eligibilityDropdownOptions,
            handleEligibilityChange
          )}
        </Layout.Item>
        <Layout.Item default="1/5" s="1/1" className="u-hide u-mt u-mt-none@s">
          <ModalOpenButton
            t={t}
            setOpenModal={setOpenModal}
            setInfoToRender={setInfoToRender}
            modalInfo={InfoToRenderType.BONUS_DEALER_PART}
          />
        </Layout.Item>
      </Layout>
    </Layout.Item>
  );

  return (
    <>
      <ContentSection className="u-pv-none">
        <ContentSection.ComponentWrapper>
          <Heading level={5}>{t('step3:financialProductSection:title')}</Heading>
          <Fieldset>
            <Fieldset.Row>
              <Layout className="o-layout--block-center">
                <Layout.Item default="1/4" s="1/1">
                  <DistributionModel
                    disabled={isFormDisabled}
                    customerType={customerType}
                    t={t}
                    setFieldValue={setFieldValue}
                  />
                </Layout.Item>
                <Layout.Item default="1/4" s="1/1">
                  {inputs.financialProduct(isFormDisabled, financialProductsOptions, handleProductChange)}
                </Layout.Item>

                <Layout.Item default="1/4" s="1/1">
                  <Layout>
                    <Layout.Item default="3/4" s="1/1">
                      {inputs.financedAmount(isFormDisabled, isCarUsed, calculateOnDiscountChange)}
                    </Layout.Item>
                    <Layout.Item default="1/4" s="1/1" className="u-mt u-mt-none@s">
                      {!isCreditBailProductForOldCarSelected && (
                        <ModalOpenButton
                          t={t}
                          setOpenModal={setOpenModal}
                          setInfoToRender={setInfoToRender}
                          modalInfo={InfoToRenderType.VEHICLE_TOTAL_COST}
                          disabled={isCarUsed}
                        />
                      )}
                    </Layout.Item>
                  </Layout>
                </Layout.Item>
                {isProductForOldCarSelected ? (
                  <Layout.Item default="1/4" s="1/1" />
                ) : (
                  <Layout.Item default="1/4" s="1/1" className="u-mt-small">
                    <ModalOpenButton
                      setOpenModal={setOpenModal}
                      setInfoToRender={setInfoToRender}
                      t={t}
                      modalInfo={InfoToRenderType.SUMMARY_OF_VEHICLE}
                      icon="semantic-forward"
                      link
                      text={t('step3:vehicleDetailsButton')}
                    />
                  </Layout.Item>
                )}
                <Layout.Item default="1/6" s="1/1">
                  <Duration
                    t={t}
                    setFieldValue={setFieldValue}
                    values={values}
                    disabled={isFormDisabled}
                    durationOptions={durationAllowedValues}
                    mileageAllowedValues={mileageAllowedValues}
                  />
                </Layout.Item>
                <Layout.Item default="1/5" s="1/1">
                  {inputs.contractMileage(isFormDisabled, mileageAllowedValues)}
                </Layout.Item>
                {!isLCDProductSelected && (
                  <Layout.Item default="1/4" s="1/1">
                    <Layout>
                      <Layout.Item default="3/4" s="1/1">
                        {inputs.annualMileage(isFormDisabled, mileageAllowedValues)}
                      </Layout.Item>
                      <Layout.Item default="1/4" s="1/1" className="u-mt u-mt-none@s">
                        <ModalOpenButton
                          t={t}
                          setOpenModal={setOpenModal}
                          setInfoToRender={setInfoToRender}
                          modalInfo={InfoToRenderType.MILEAGE_PRICE_AMOUNT}
                          disabled={isLvdlProductSelected}
                        />
                      </Layout.Item>
                    </Layout>
                  </Layout.Item>
                )}

                {isCreditBailProductSelected && (
                  <>
                    <Layout.Item default="1/6" s="1/1">
                      {inputs.contractRVAmount(isFormDisabled, calculateOnContractRVChange)}
                    </Layout.Item>
                    <Layout.Item default="1/6" s="1/1">
                      {inputs.contractRVPercentage(isFormDisabled, calculateOnContractRVChange)}
                    </Layout.Item>
                  </>
                )}
                {isLCDProductSelected && (
                  <>
                    <Layout.Item default="1/6" s="1/1">
                      {inputs.overruledRV(isFormDisabled, isLCDProduct(selectedProduct), calculateOnOverruledRVChange)}
                    </Layout.Item>
                    <Layout.Item default="1/6" s="1/1">
                      {inputs.overruledRVNet()}
                    </Layout.Item>
                    <Layout.Item default="1/6" s="1/1">
                      {inputs.overruledRVPercentage()}
                    </Layout.Item>
                  </>
                )}
                {showOverruledRVs && !isLCDProduct(selectedProduct) && (
                  <>
                    <Layout.Item default="1/6" s="1/1">
                      {inputs.recommendedRV()}
                    </Layout.Item>
                    <Layout.Item default="1/6" s="1/1">
                      {inputs.overruledRV(isFormDisabled, isLCDProduct(selectedProduct))}
                    </Layout.Item>
                  </>
                )}
              </Layout>
            </Fieldset.Row>
          </Fieldset>
        </ContentSection.ComponentWrapper>

        <Hr className="u-bg-alternative" />

        <ContentSection.ComponentWrapper>
          <Heading level={5}>{t('step3:pricingSection:title')}</Heading>

          <Fieldset>
            <Fieldset.Row>
              <Layout>
                {!isLCDProductSelected && (
                  <Layout.Item default="1/1">{inputs.firstInstallmentIsIncreased(isFormDisabled)}</Layout.Item>
                )}
                {values.firstInstallmentIsIncreased && !isLCDProductSelected && (
                  <Layout.Item s="1/1">
                    <Layout>
                      <Layout.Item default="1/3" s="1/1">
                        <Layout>
                          <Layout.Item default="3/5" s="1/1">
                            {inputs.firstInstallmentAmount(isFormDisabled, values.financedAmount)}
                          </Layout.Item>
                          <Layout.Item default="2/5" s="1/1">
                            {inputs.firstInstallmentAmountPercentage(isFormDisabled)}
                          </Layout.Item>
                        </Layout>
                      </Layout.Item>
                      <Layout.Item default="1/3" s="1/1">
                        <Layout>
                          <Layout.Item default="3/5" s="1/1">
                            {inputs.downPaymentAmount(isFormDisabled, maxDownPaymentAmount)}
                          </Layout.Item>
                          <Layout.Item default="2/5" s="1/1">
                            {inputs.downPaymentAmountPercentage(isFormDisabled, maxDownPaymentAmountPercentage)}
                          </Layout.Item>
                        </Layout>
                      </Layout.Item>
                      <Layout.Item default="1/3" s="1/1">
                        <Layout>
                          <Layout.Item default="3/5" s="1/1">
                            {inputs.firstInstallmentWithDownPaymentAmount()}
                          </Layout.Item>
                          <Layout.Item default="2/5" s="1/1">
                            {inputs.firstInstallmentWithDownPaymentPercentage()}
                          </Layout.Item>
                        </Layout>
                      </Layout.Item>
                      <Layout.Item default="1/3" s="1/1">
                        <TotalAmountInsAndServ t={t} />
                      </Layout.Item>
                    </Layout>
                  </Layout.Item>
                )}
                <Layout className="u-1/1 u-pl u-mt-none">
                  <Layout.Item default="1/4" s="1/1">
                    <PricingStrategyCombobox
                      promotions={promotions}
                      onPricingStrategyChange={(milesPricingStrategy, deselectedPromotions, selectedPromotions) => {
                        calculateQuoteAuto(
                          quoteId,
                          getCalculateRequest(
                            {
                              ...values,
                              milesPricingStrategy,
                              deselectedPromotions,
                              selectedPromotions,
                            },
                            selectedAllAdditionalProducts
                          )
                        );
                      }}
                      disabled={isFormDisabled}
                    />
                  </Layout.Item>
                  {values.milesPackage && (
                    <Layout.Item default="1/4" s="1/1">
                      <MilesPackage
                        disabled={isFormDisabled}
                        calculate={(newValue) =>
                          calculateQuoteAuto(
                            quoteId,
                            getCalculateRequest(
                              stripPromotionsFromValues({ ...values, milesPackage: newValue }),
                              selectedAllAdditionalProducts
                            )
                          )
                        }
                      />
                    </Layout.Item>
                  )}
                  <Layout.Item default="1/4" s="1/1">
                    {inputs.pricingId()}
                  </Layout.Item>

                  {isLCDProductSelected && (
                    <Layout.Item default="1/4" s="1/1">
                      <WithdrawalDate t={t} isDisabled={isFormDisabled || customerType === CustomerType.LEGAL_ENTITY} />
                    </Layout.Item>
                  )}

                  {!isLvdlProductSelected && !isLCDProductSelected && renderEligibilitySection}
                </Layout>
                <Layout className="u-1/1 u-pl u-mt-none">
                  {!isLCDProductSelected && (
                    <>
                      <Layout.Item default="1/4" s="1/1">
                        <WithdrawalDate
                          t={t}
                          isDisabled={isFormDisabled || customerType === CustomerType.LEGAL_ENTITY}
                        />
                      </Layout.Item>

                      <Layout.Item default="1/4" s="1/1">
                        {inputs.numberOfMonthlyInstallments()}
                      </Layout.Item>

                      <Layout.Item default="1/4" s="1/1">
                        {inputs.subsequentInstallment()}
                      </Layout.Item>
                    </>
                  )}
                </Layout>
              </Layout>
            </Fieldset.Row>
          </Fieldset>
        </ContentSection.ComponentWrapper>

        {!isLCDProductSelected && (
          <>
            <Hr className="u-bg-alternative" />

            <ContentSection.ComponentWrapper>
              <Heading level={5}>{t('step3:insurancesServicesSection:title')}</Heading>

              <Layout className="u-pl">
                <Layout.Item default="1/1" s="1/1" className="u-pl-none">
                  <Services
                    disabled={isFormDisabled}
                    selectedAdditionalProducts={selectedAllAdditionalProducts}
                    setSelectedAllAdditionalProducts={setSelectedAllAdditionalProducts}
                    resetFuelPrices={resetFuelPrices}
                    customerType={customerType}
                    managementFees={managmentFees}
                    fuelDepositAmount={fuelDepositAmount}
                    setSelectedInsuranceApplicantOption={setSelectedInsuranceApplicantOption}
                    selectedInsuranceApplicantOption={selectedInsuranceApplicantOption}
                    hasCoCustomer={hasCoCustomer}
                  />
                </Layout.Item>
                <Layout.Item default="1/3" s="1/1" className="u-pl-none">
                  {inputs.totalServicesCost()}
                </Layout.Item>
              </Layout>

              <Notification
                title={t(`step3:milesWarningModal:title`)}
                visible={showNotification}
                status="info"
                onCloseClick={() => {
                  setShowNotification(false);
                  resetMilesWarnings();
                }}
                className="u-mt"
              >
                {milesWarnings?.map((warning) => <Paragraph key={warning.id}>{warning.description}</Paragraph>)}
              </Notification>
            </ContentSection.ComponentWrapper>

            <Hr className="u-bg-alternative" />

            <ContentSection.ComponentWrapper>
              <Heading level={5}>{t('step3:fiscalInformation:title')}</Heading>
              <Fieldset>
                {benefitPackage && (
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="1/3" s="1/1">
                        {inputs.annualDepreciationNonDeductible()}
                      </Layout.Item>
                    </Layout>
                    <Layout>
                      <Layout.Item default="1/3" s="1/1">
                        {inputs.annualCo2Tax()}
                      </Layout.Item>
                    </Layout>
                    <Layout>
                      <Layout.Item default="1/3" s="1/1">
                        {inputs.annualTaxOnAirPollutants()}
                      </Layout.Item>
                    </Layout>
                    {isElectricEngine ? (
                      <Layout>
                        <Layout.Item default="1/3" s="1/1">
                          {inputs.fringeBenefitElectricCars()}
                        </Layout.Item>
                      </Layout>
                    ) : (
                      <>
                        <Layout>
                          <Layout.Item default="1/3" s="1/1">
                            {inputs.fringeBenefitFuelExcluded()}
                          </Layout.Item>
                        </Layout>

                        <Layout>
                          <Layout.Item default="1/3" s="1/1">
                            {inputs.fringeBenefitFuelIncluded()}
                          </Layout.Item>
                        </Layout>
                      </>
                    )}
                  </Fieldset.Row>
                )}
                <Fieldset.Row>
                  <Layout>
                    <Layout.Item default="1/3" s="1/1">
                      {inputs.milesBonusEcologic(isFormDisabled)}
                    </Layout.Item>
                  </Layout>
                </Fieldset.Row>
                {showPrimeCee && (
                  <Fieldset.Row>
                    <Layout>
                      <Layout.Item default="1/3" s="1/1">
                        {inputs.milesPrimeCee(isFormDisabled)}
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                )}
              </Fieldset>
            </ContentSection.ComponentWrapper>
          </>
        )}
        <Hr className="u-bg-alternative" />
      </ContentSection>

      <PriceBox
        header={isLCDProductSelected ? t('step3:leasePriceNoServices:label') : t('step3:spreadLeasePrice:label')}
        price={
          <>
            <span>{spreadLeasePrice}</span>
            <Button
              action
              name="calculation"
              type="button"
              onClick={onClickOnCalculateTotal}
              style={{
                // Workaround: PriceBox buttons have varying widths based on label length.
                // minWidth ensures both "OK" and "Calculer" render at a consistent size.
                minWidth: '14ch',
                // Highlight the "Calculate" button dark-yellow when shouldCalculate is true
                backgroundColor: shouldCalculate && isQuoteEditable ? '#d8aa00' : undefined,
              }}
              disabled={isFormDisabled}
              className="u-ml-small u-mb-xxsmall"
            >
              {shouldCalculate && isQuoteEditable ? t('step3:calculation') : t('step3:ok')}
            </Button>
          </>
        }
        standalone
      />

      {/* PAGE BUTTONS */}
      <Fieldset>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/1" s="1/1" className="u-mt">
              <SaveAndQuoteDocumentsButtons
                setOpenModal={setOpenPrintAndSaveModal}
                setOpenCopyQuoteModal={setOpenCopyQuoteModal}
                setInfoToRender={setInfoToRender}
                shouldCalculate={shouldCalculate}
              />
            </Layout.Item>
            <Layout.Item default="1/1" s="1/1" />
            <CustomModal
              openModal={openModal}
              setOpenModal={setOpenModal}
              confirmText={buildModalConfirmText(t, infoToRender)}
              title={buildModalTitle(t, infoToRender)}
              cancelText=""
              center={getModalCenterContent(infoToRender)}
            >
              {(() => {
                if (!openModal) return null;

                switch (infoToRender) {
                  case InfoToRenderType.MILEAGE_PRICE_AMOUNT:
                    return (
                      <Layout className="u-mb" center>
                        <Layout.Item default="1/3" s="1/1">
                          {inputs.excessMileagePriceAmount(
                            isLouProductSelected || isLvdlProductSelected || isFormDisabled
                          )}
                        </Layout.Item>
                        {showDamagesWaiverField && (
                          <Layout.Item default="1/3" s="1/1">
                            <DamagesWaiver disabled={isFormDisabled} />
                          </Layout.Item>
                        )}
                      </Layout>
                    );
                  case InfoToRenderType.VEHICLE_TOTAL_COST:
                    return (
                      <Layout>
                        {!isCreditBailProductForOldCarSelected && (
                          <>
                            <Layout.Item default="1/2" s="1/1">
                              {inputs.vehicleTotalCost()}
                            </Layout.Item>
                            <Layout.Item default="1/2" s="1/1">
                              <Layout>
                                <Layout.Item default="3/5" s="1/1">
                                  {inputs.totalDiscountAmount(
                                    values._minDealerDiscountAmount,
                                    calculateOnDiscountChange,
                                    isFormDisabled
                                  )}
                                </Layout.Item>
                                <Layout.Item default="2/5" s="1/1">
                                  {inputs.totalDiscountPercentage(
                                    values._minDealerDiscountPercentage,
                                    calculateOnDiscountChange,
                                    isFormDisabled
                                  )}
                                </Layout.Item>
                              </Layout>
                            </Layout.Item>
                          </>
                        )}
                        <Layout.Item default="1/2" s="1/1" />
                      </Layout>
                    );
                  case InfoToRenderType.BONUS_DEALER_PART:
                    return (
                      <Layout center className="u-1/1 u-text-center u-pl-large@xl u-pl-none@l u-pl-none@m u-pl-none@s">
                        <Layout.Item default="1/4" s="1/1" l="1/4">
                          {inputs.serviceBonus()}
                        </Layout.Item>
                        <Layout.Item default="2/4" s="1/1" l="2/4">
                          <Layout>
                            <Layout.Item default="1/2" s="1/1" l="1/2" m="1/2">
                              {inputs.dealerEffort(isFormDisabled, values.dealerEffortMax)}
                            </Layout.Item>
                            <Layout.Item default="1/2" s="1/1" l="1/2" m="1/2">
                              {inputs.dealerEffortMax()}
                            </Layout.Item>
                          </Layout>
                        </Layout.Item>
                        <Layout.Item default="1/1" />
                      </Layout>
                    );
                  case InfoToRenderType.SUMMARY_OF_VEHICLE:
                    return <VehicleDetails isFormDisabled={isFormDisabled} t={t} values={values} />;
                  case InfoToRenderType.LCD_WARNING:
                    return (
                      <Layout className="u-mb" center>
                        <Layout.Item default="1/1" s="1/1">
                          <Paragraph className="u-mb-none">{t('step3:lcdProductWarning:row1')}</Paragraph>
                          <Paragraph className="u-mb-none">{t('step3:lcdProductWarning:row2')}</Paragraph>
                        </Layout.Item>
                      </Layout>
                    );
                  case InfoToRenderType.PUSH_VO_WARNING:
                    return (
                      <Layout className="u-mb" center>
                        <Layout.Item default="1/1" s="1/1">
                          <Paragraph className="u-mb-none">{t('step3:pushVoWarning:row1')}</Paragraph>
                          <Paragraph className="u-mb-none">{t('step3:pushVoWarning:row2')}</Paragraph>
                        </Layout.Item>
                      </Layout>
                    );
                  default:
                    return null;
                }
              })()}
            </CustomModal>
          </Layout>
        </Fieldset.Row>
      </Fieldset>
      <CopyQuoteModal
        shown={openCopyQuoteModal}
        onConfirm={async () => {
          await handleCopyQuote(quoteId, navigate, 3);
          setOpenCopyQuoteModal(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        text={t('step3:copyModal:copyTextTitle')}
        onClose={() => setOpenCopyQuoteModal(false)}
      />
      <DocumentsPrintAndEmailSendoutModal
        t={t}
        quoteId={quoteId}
        setOpenModal={setOpenPrintAndSaveModal}
        shown={openPrintAndSaveModal && infoToRender === InfoToRenderType.DOCUMENTS_PRINTING}
      />
    </>
  );
};
